import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import LotteryCodeField from '../../components/LotteryCodeField';
import DateField from '../../components/DateField';
import ScopeField from '../../components/ScopeField';
import CumulDetailleResultsView from './CumulDetailleResultsView';
import { fetchCumulDetaille } from '../../api/cumulDetaille';
import { downloadXLSX } from '../../utils/xlsx';
import { todayISO } from '../../utils/date';
import './CumulDetaillePage.css';

const isoToDisplay = (iso) => {
  const [year, month, day] = iso.split('-');
  return `${day}/${month}/${year}`;
};

const perteGainOptions = ['Tout', 'Perte', 'Gain'];
const perteGainApiValue = { Tout: 'All', Perte: 'Loss', Gain: 'Win' };
const segmentadoPorOptions = ['Grupos', 'Riferos', 'Bancas', 'Loterias'];

const CumulDetaillePage = () => {
  const { company } = useAuth();
  const [filter, setFilter] = useState('Tout');
  const [scopeId, setScopeId] = useState('');
  const [scopeName, setScopeName] = useState('');
  const [dateFrom, setDateFrom] = useState(todayISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [lotteryFrom, setLotteryFrom] = useState('0');
  const [lotteryFromName, setLotteryFromName] = useState('');
  const [lotteryTo, setLotteryTo] = useState('0');
  const [lotteryToName, setLotteryToName] = useState('');
  const [detLoteries, setDetLoteries] = useState(false);
  const [perteGain, setPerteGain] = useState('Tout');
  const [segmentadoPor, setSegmentadoPor] = useState('');
  const [showResults, setShowResults] = useState(false);

  const [rows, setRows] = useState([]);
  const [grandTotal, setGrandTotal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSearch = async (e) => {
    e.preventDefault();
    if (filter !== 'Tout' && !scopeId) {
      setError(`Choisis d'abord le ${filter.toLowerCase()} (N°, nom ou 🔍).`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchCumulDetaille({
        filter,
        scope: filter !== 'Tout' ? scopeId : undefined,
        dateFrom,
        dateTo,
        lotteryFrom: lotteryFrom !== '0' ? lotteryFrom : undefined,
        lotteryUp: lotteryTo !== '0' ? lotteryTo : undefined,
        perteGain: perteGainApiValue[perteGain] || 'All',
      });
      setRows(data.rows);
      setGrandTotal(data.grand_total);
      setShowResults(true);
    } catch (err) {
      setError('Recherche impossible.');
    } finally {
      setLoading(false);
    }
  };

  const handleExcelExport = () => {
    const header = [
      'Banque (ID POS)', 'Entreprise / Username', 'Vendu', 'Gain',
      'Commission', 'Épargne', 'Tax', 'Profit/Perte',
    ];
    const dataRows = rows.map((r) => [
      `${r.bank_seq} - ${r.bank_name}`,
      r.bank_username || r.device_serial ? `${r.company_name} (${r.bank_username || r.device_serial})` : r.company_name,
      r.vendu, r.gagne, r.commission, r.epargne, '—', r.total,
    ]);
    const totalRow = grandTotal ? [
      'Total', '', grandTotal.vendu, grandTotal.gagne,
      grandTotal.commission, grandTotal.epargne, '—', grandTotal.total,
    ] : [];
    const sheet = [
      [company?.name || ''],
      ['Ventes Cumulees detaillee par Banque'],
      [`De: ${isoToDisplay(dateFrom)} A: ${isoToDisplay(dateTo)} | Loterie : ${lotteryFromName || 'Tout'}`],
      [],
      header,
      ...dataRows,
      totalRow,
    ];
    downloadXLSX(`cumul-detaille_${dateFrom}_${dateTo}.xlsx`, sheet, {
      sheetName: 'Cumul detaille',
      headerRow: 4,
      boldRows: [0, 1, sheet.length - 1],
    });
  };

  if (showResults) {
    return (
      <CumulDetailleResultsView
        rows={rows}
        grandTotal={grandTotal}
        dateFrom={isoToDisplay(dateFrom)}
        dateTo={isoToDisplay(dateTo)}
        loterieLabel={lotteryFromName || 'Tout'}
        generatedAt={new Date().toISOString().slice(0, 19).replace('T', ' ')}
        onBack={() => setShowResults(false)}
        onExcel={handleExcelExport}
      />
    );
  }

  return (
    <Layout>
      <p className="cd-breadcrumb">
        Ruta : <Link to="/dashboard" className="bc-link">Menu</Link>
        {' >> '}
        <Link to="/rapports" className="bc-link">Rapports</Link>
        {' >> '}
        <span className="bc-current">Cumul detaille</span>
      </p>

      <div className="cd-card">
        <div className="cd-card-header">
          <span className="cd-card-title">Vente Detaillee Accumulee</span>
        </div>
        <div className="cd-card-body">
          <div className="cd-radio-row">
            {['Tout', 'Groupe', 'Superviseur', 'Banque'].map((opt) => (
              <label key={opt} className="cd-radio-label">
                <input
                  type="radio"
                  name="filter"
                  value={opt}
                  checked={filter === opt}
                  onChange={() => { setFilter(opt); setScopeId(''); setScopeName(''); }}
                />
                {opt}
              </label>
            ))}
          </div>

          {filter !== 'Tout' && (
            <div className="cd-row cd-scope-row">
              <label className="cd-label">{filter}</label>
              <ScopeField
                filter={filter}
                id={scopeId}
                name={scopeName}
                onResolve={(id, nm) => { setScopeId(id); setScopeName(nm); }}
                inputClass="cd-input cd-input-sm"
                iconClass="cd-icon-btn"
                nameClass="cd-lottery-name"
              />
            </div>
          )}

          <form onSubmit={handleSearch} className="cd-form">
            <div className="cd-row">
              <label className="cd-label">De</label>
              <DateField className="cd-input cd-input-date" value={dateFrom} onChange={setDateFrom} />

              <label className="cd-label cd-label-to">A</label>
              <DateField className="cd-input cd-input-date" value={dateTo} onChange={setDateTo} />
            </div>

            <div className="cd-row">
              <label className="cd-label">Loterie De</label>
              <LotteryCodeField
                value={lotteryFrom}
                name={lotteryFromName}
                onChange={setLotteryFrom}
                onResolve={(code, name) => { setLotteryFrom(code); setLotteryFromName(name); }}
                inputClass="cd-input cd-input-sm"
                iconClass="cd-icon-btn"
                nameClass="cd-lottery-name"
              />

              <label className="cd-label cd-label-to">Loterie Up</label>
              <LotteryCodeField
                value={lotteryTo}
                name={lotteryToName}
                onChange={setLotteryTo}
                onResolve={(code, name) => { setLotteryTo(code); setLotteryToName(name); }}
                inputClass="cd-input cd-input-sm"
                iconClass="cd-icon-btn"
                nameClass="cd-lottery-name"
              />
            </div>


            <div className="cd-row">
              <label className="cd-label">Det Loteries</label>
              <input
                type="checkbox"
                checked={detLoteries}
                onChange={e => setDetLoteries(e.target.checked)}
              />
            </div>

            <div className="cd-row">
              <label className="cd-label">Profit/Perte</label>
              <select className="cd-select" value={perteGain} onChange={e => setPerteGain(e.target.value)}>
                {perteGainOptions.map(opt => <option key={opt}>{opt}</option>)}
              </select>
            </div>

            <div className="cd-row">
              <label className="cd-label">Segmentado por:</label>
              <select className="cd-select" value={segmentadoPor} onChange={e => setSegmentadoPor(e.target.value)}>
                <option value=""></option>
                {segmentadoPorOptions.map(opt => <option key={opt}>{opt}</option>)}
              </select>
            </div>

            {error && <p className="cd-error">{error}</p>}

            <div className="cd-btn-row">
              <button type="submit" className="cd-search-btn" disabled={loading}>
                {loading ? 'Recherche...' : 'Chercher'}
              </button>
              <button type="button" className="cd-excel-btn" onClick={handleExcelExport}>Excel</button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default CumulDetaillePage;
